import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Button,
  Table,
  TableInput,
  TableSelect,
  Checkbox,
  Snackbar,
  IconButton,
  Pagination,
} from "@narmi/design_system";
import Container from "../../Container";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";
import {
  COL_LAYOUT,
  COL_VISIBILITY,
  READ_ONLY_COL_LAYOUT,
  READ_ONLY_COL_VISIBILITY,
} from "./recipientReviewConstants";
import { useRecipientReview } from "./useRecipientReview";
import type { RowEdit } from "./recipientReviewTypes";
import styles from "./RecipientReviewScreen.module.scss";

const PAGE_SIZE = 20;

const RecipientReviewScreen = () => {
  const navigate = useNavigate();
  const {
    l10n,
    nachaResponse,
    newRecipientRows,
    canSaveRecipients,
    rowEdits,
    rowErrors,
    rowRecipients,
    selected,
    saved,
    nextLoading,
    hasAnyErrors,
    errorCategories,
    allSelected,
    someSelected,
    handleFieldChange,
    handleFieldBlur,
    handleToggleSelectAll,
    handleToggleRow,
    handleSaveRecipient,
    handleSaveAll,
    handleClearSelection,
    handleBack,
    handleNext,
    handleAccountTypeChange,
    getTagClassName,
    getTagLabel,
    canSaveRow,
  } = useRecipientReview();

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(newRecipientRows.length / PAGE_SIZE);
  const paginatedRows = newRecipientRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const pageOffset = (currentPage - 1) * PAGE_SIZE;

  // Redirect to upload screen if no data
  useEffect(() => {
    if (!nachaResponse) {
      navigate(`/${PAYMENT_ACTIVITY_ROUTE}/upload`, { replace: true });
    }
  }, [nachaResponse, navigate]);

  if (!nachaResponse) {
    return null;
  }

  // --- Render a text field cell ---
  const renderTextField = (
    idx: number,
    editField: keyof RowEdit,
    errorKey: keyof RowEdit | "name",
    placeholder: string
  ) => {
    const edit = rowEdits[idx];
    const errors = rowErrors[idx] ?? {};
    const hasError = Boolean(errors[errorKey]);

    return (
      <div onBlur={() => handleFieldBlur(idx, editField)}>
        <TableInput
          value={edit[editField]}
          label={placeholder}
          placeholder={placeholder}
          onChange={(e) => handleFieldChange(idx, editField, e.target.value)}
          hasError={hasError}
        />
      </div>
    );
  };

  return (
    <Container maxWidth="1185px">
      <div className={styles.pageHeader}>
        <h4>{l10n.getString("recipient-review-header")}</h4>
      </div>
      <div className={styles.sectionWrapper}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>
            <span
              className="narmi-icon-user-plus"
              style={{ fontSize: "24px" }}
            />
          </div>
          <div className={styles.sectionTextBlock}>
            <div className={styles.sectionTitle}>
              <span className="fontSize--l fontWeight--bold">
                {l10n.getString("recipient-review-section-title")}
              </span>
              <span className={styles.badge}>{newRecipientRows.length}</span>
            </div>
            <span className="fontColor--secondary">
              {canSaveRecipients
                ? l10n.getString("recipient-review-section-description")
                : l10n.getString(
                    "recipient-review-section-description-no-permission"
                  )}
            </span>
          </div>
        </div>
        {hasAnyErrors && (
          <div className={styles.errorBanner}>
            <span
              className={`narmi-icon-alert-circle ${styles.errorBannerIcon}`}
            />
            <div className={styles.errorBannerContent}>
              <span>{l10n.getString("recipient-review-error-banner")}</span>
              <ul className={styles.errorBannerList}>
                {errorCategories.includes("required") && (
                  <li>
                    {l10n.getString("recipient-review-error-required-fields")}
                  </li>
                )}
                {errorCategories.includes("account_number") && (
                  <li>
                    {l10n.getString("recipient-review-error-invalid-account")}
                  </li>
                )}
                {errorCategories.includes("routing_number") && (
                  <li>
                    {l10n.getString("recipient-review-error-invalid-routing")}
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}
        <div className={styles.tableWrapper}>
          <Table
            kind="editable"
            colVisibility={
              canSaveRecipients ? COL_VISIBILITY : READ_ONLY_COL_VISIBILITY
            }
            colLayout={canSaveRecipients ? COL_LAYOUT : READ_ONLY_COL_LAYOUT}
          >
            <Table.Header>
              <Table.Row>
                {canSaveRecipients && (
                  <Table.HeaderCell>
                    <Checkbox
                      checked={allSelected}
                      onChange={handleToggleSelectAll}
                      indeterminate={someSelected}
                    />
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell>
                  {l10n.getString("recipient-review-col-name")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("recipient-review-col-account")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("recipient-review-col-routing")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  <span className={styles.typeHeaderCell}>
                    {l10n.getString("recipient-review-col-type")}
                  </span>
                </Table.HeaderCell>
                {canSaveRecipients && (
                  <Table.HeaderCell>{null}</Table.HeaderCell>
                )}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paginatedRows.map((_row, pageIdx) => {
                const idx = pageOffset + pageIdx;
                const edit = rowEdits[idx];
                if (!edit) return null;
                const errors = rowErrors[idx] ?? {};

                return (
                  <Table.Row key={idx}>
                    {canSaveRecipients && (
                      <Table.Cell>
                        <Checkbox
                          checked={!saved.has(idx) && selected.has(idx)}
                          onChange={() => handleToggleRow(idx)}
                          disabled={saved.has(idx) || !rowRecipients[idx]}
                        />
                      </Table.Cell>
                    )}
                    <Table.Cell>
                      {renderTextField(
                        idx,
                        "recipient_name",
                        "name",
                        l10n.getString("recipient-review-placeholder-name")
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {renderTextField(
                        idx,
                        "account_number",
                        "account_number",
                        l10n.getString("recipient-review-placeholder-account")
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {renderTextField(
                        idx,
                        "routing_number",
                        "routing_number",
                        l10n.getString("recipient-review-placeholder-routing")
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <TableSelect
                        id={`recipient-review-account-type-${idx}`}
                        label={l10n.getString("recipient-review-col-type")}
                        value={edit.account_type}
                        onChange={(value: string) =>
                          handleAccountTypeChange(idx, value)
                        }
                        hasError={Boolean(errors.account_type)}
                      >
                        <TableSelect.Item value="checking">
                          Checking
                        </TableSelect.Item>
                        <TableSelect.Item value="savings">
                          Savings
                        </TableSelect.Item>
                      </TableSelect>
                    </Table.Cell>
                    {canSaveRecipients && (
                      <Table.Cell>
                        <button
                          type="button"
                          className={getTagClassName(idx, styles)}
                          onClick={() => handleSaveRecipient(idx)}
                          disabled={!canSaveRow(idx)}
                        >
                          {getTagLabel(idx)}
                        </button>
                      </Table.Cell>
                    )}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
          {totalPages > 1 && (
            <div className={styles.paginationWrapper}>
              <Pagination
                totalPages={totalPages}
                selectedPage={currentPage}
                onPageChange={(page: number) => setCurrentPage(page)}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.footerActions}>
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <button
              type="button"
              className={styles.backLink}
              onClick={handleBack}
            >
              {l10n.getString("recipient-review-back")}
            </button>
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={
                nextLoading
                  ? l10n.getString("recipient-review-next-loading")
                  : l10n.getString("button-next")
              }
              disabled={hasAnyErrors || nextLoading}
              onClick={handleNext}
            />
          </Row.Item>
        </Row>
      </div>
      <div className={styles.stickyContainer}>
        <Snackbar isActive={selected.size > 0}>
          <span className="fontColor--primary fontWeight--semibold">
            {l10n.getString("recipient-review-snackbar-selected", {
              count: selected.size,
            })}
          </span>
          <IconButton
            kind="action"
            label={l10n.getString("recipient-review-clear-selection")}
            name="x"
            textSize="l"
            onClick={handleClearSelection}
          />
          <Snackbar.Divider />
          <Snackbar.ButtonGroup>
            <Button
              kind="primary"
              label={l10n.getString("recipient-review-save-all")}
              onClick={handleSaveAll}
            />
          </Snackbar.ButtonGroup>
        </Snackbar>
      </div>
    </Container>
  );
};

export default RecipientReviewScreen;
